<template>
  <section class="call-to-action">
    <div class=" container">
      <div class="call-to-action-mid">
        <img class="call-to-action-mid-img object-center" :src="data.img" />
        <div class="call-to-action-mid-content">
          <h2 class="call-to-action-mid-content-title">{{ data.small_title }}</h2>
          <div class="call-to-action-mid-content-text" v-html="data.main_header" />
          <Button
            :url="data.cta.url"
            :text="data.cta.title"
            color="green-dark"
            lineHover="bg-mint"
            circleColor="circle-orange"
          />
        </div>
      </div>
      <div class="call-to-action-btm">
        <div class="call-to-action-btm-decoration">
          <img
            class="call-to-action-btm-decoration-image"
            src="@/assets/img/three_circles_decoration.svg"
          />
        </div>
        <div class="call-to-action-btm-content">
          <h3 class="call-to-action-btm-content-title">{{ data.content_title }}</h3>
          <div class="call-to-action-btm-content-text" v-html="data.content"></div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import Button from '@/components/utility/Button.vue'
import { onMounted, ref, onBeforeUnmount } from 'vue'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'

const parallax = ref()

gsap.registerPlugin(ScrollTrigger)
const animateDecoration = () => {
  gsap.to('.call-to-action-btm-decoration-image', {
    rotate: 180 * 3,
    scrollTrigger: {
      id: 'call-to-action-anim',
      trigger: '.call-to-action',
      start: 'top top',
      end: 'bottom top',
      scrub: 0.5
    }
  })
}
defineProps(['data'])
onMounted(() => {
  animateDecoration()
  // ScrollTrigger.refresh()
})

onBeforeUnmount(() => {
  if (parallax.value) parallax.value.destroy()

  const sectionAnim = ScrollTrigger.getById('call-to-action-anim')
  if (sectionAnim) sectionAnim.kill()
})
</script>

<style lang="scss" scoped>
.call-to-action {
    @apply relative z-auto;
    &::before {
        @apply bg-ore_mint;
        content: "";
        width: 100%;
        height: 100%;
        position: absolute;
        left: 0;
        top: 0;
        z-index: 0;
    }

  .container {
    @apply z-1 relative w-full max-w-screen-xl
    mx-auto px-[10px] py-[50px]
    flex flex-col items-center
    md:px-[60px] md:pb-[120px]
    xl:pt-[135px] xl:pb-[200px] xl:px-0;

    .big-call-to-action {
      @apply px-[15px] w-full max-w-[1192px] max-h-[377px] mb-[50px]
      md:px-0 md:mb-[100px]
      xl:mb-[150px];
    }
    .call-to-action-mid {
      @apply flex flex-col-reverse mb-[75px] gap-[62px]
      md:mb-[150px]
      lg:px-[14px]
      xl:gap-[165px]
      sm:flex-row;
      &-img {
        @apply rounded-[27px] object-cover relative
        min-h-[400px]
        sm:min-h-[300px] sm:max-w-[305px] sm:max-h-[300px]
        md:w-[300px] md:h-[300px] md:max-w-none md:max-h-full
        xl:w-[476px] xl:h-[485px];
      }
      &-content {
        @apply px-[15px] md:flex basis-full lg:basis-7/12
        md:px-0 md:flex-col md:justify-center md:mt-5;
        &-title {
          @apply mb-9
          text-[20px] leading-none mt-10
          sm:mb-11
          md:text-[30px] md:leading-[30px] md:mt-0;
        }
        &-text {
          @apply text-[30px] leading-[30px] mb-10
          md:mb-5
          lg:text-[70px] lg:leading-[70px] lg:mb-10;
        }
        .button {
          @apply pl-0
          ;
        }
      }
    }
    .call-to-action-btm {
      @apply px-[15px] mb-12 w-full relative flex flex-col items-center gap-[100px] text-[20px] leading-none
      md:px-0 md:flex-row md:gap-[40px]
      lg:px-[14px]
      lg:justify-between
      xl:justify-start
      xl:gap-[380px] xl:text-[30px];
      &-decoration {
        @apply absolute right-[15px] bottom-2 lg:mx-auto
        md:relative md:pl-[30px] md:-mt-9
        md:self-center
        lg:self-start lg:mt-2
        xl:pl-[200px];
        img {
          @apply min-w-[59px] min-h-[55px];
        }
      }
      &-content {
        @apply flex flex-col gap-[30px]
        md:gap-[40px]
        lg:w-[calc(100%_-_360px)]
        ;

        &-title{
          @apply text-[24px] leading-none md:text-[20px] lg:text-[30px];
        }
        &-text {
          @apply flex flex-col
          gap-[70px]
          md:flex-row md:gap-8 lg:gap-[30px] lg:justify-between;
           :deep(p) {
            @apply md:flex-1 lg:max-w-[320px] md:w-6/12 lg:w-[calc(50%_-_15px)];
          }
          :deep(div) {
            @apply md:flex-1 md:w-6/12 lg:w-[calc(50%_-_15px)];
            p {
              @apply w-full mb-6;
            }
            ul {
              @apply w-full;
            }
          }

          :deep(ul) {
            @apply font-medium text-[24px] pr-16 leading-none md:w-6/12 md:pr-0 lg:w-[calc(50%_-_15px)];

            li{
              @apply relative pl-6 md:pl-8;

              &:before{
                 @apply content-['→'] w-auto h-auto block absolute left-0;
              }

              &.cross {
                @apply relative ml-6 leading-none my-1 text-2xl md:pl-7;

                &:before{
                  @apply content-['+'] md:left-[6px];
                }

                +.cross {
                  @apply leading-none;
                }
              }
            }
          }
        }
      }
    }
  }
}
</style>
